import axios from 'axios';

// Use same protocol as current page to avoid mixed content errors
const currentProtocol = window.location.protocol;
const currentHostname = window.location.hostname;

// Always use environment variable - no hardcoded URLs
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

if (!BACKEND_URL) {
  throw new Error('REACT_APP_BACKEND_URL environment variable is required');
}

const API = `${BACKEND_URL}/api`;

// Create axios instance for auth
const authClient = axios.create({
  baseURL: API,
  timeout: 30000,
});

// Add token to requests automatically
authClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('rooftrack_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration
authClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('rooftrack_token');
      localStorage.removeItem('rooftrack_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  // Authentication
  async login(email, password) {
    const response = await authClient.post('/auth/login', { email, password });
    const { access_token, user } = response.data;
    
    // Store token and user info
    localStorage.setItem('rooftrack_token', access_token);
    localStorage.setItem('rooftrack_user', JSON.stringify(user));
    
    return { token: access_token, user };
  },

  async signup(userData) {
    const response = await authClient.post('/auth/signup', userData);
    const { access_token, user } = response.data;
    
    // Store token and user info
    localStorage.setItem('rooftrack_token', access_token);
    localStorage.setItem('rooftrack_user', JSON.stringify(user));
    
    return { token: access_token, user };
  },

  async requestPasswordReset(email) {
    const response = await authClient.post('/auth/password-reset-request', { email });
    return response.data;
  },

  async verifyResetToken(token) {
    const response = await authClient.post('/auth/verify-reset-token', null, {
      params: { token }
    });
    return response.data;
  },

  async resetPassword(token, new_password) {
    const response = await authClient.post('/auth/password-reset-confirm', {
      token,
      new_password
    });
    return response.data;
  },

  async logout() {
    localStorage.removeItem('rooftrack_token');
    localStorage.removeItem('rooftrack_user');
    window.location.href = '/';
  },

  async getCurrentUser() {
    const response = await authClient.get('/auth/me');
    return response.data;
  },

  async getUserPermissions() {
    const response = await authClient.get('/auth/permissions');
    return response.data.permissions;
  },

  async checkPermission(permission) {
    const response = await authClient.post('/auth/check-permission', { permission });
    return response.data.has_permission;
  },

  // User Management
  async createUser(userData) {
    const response = await authClient.post('/auth/register', userData);
    return response.data;
  },

  async getUsers(limit = 100) {
    const response = await authClient.get('/auth/users', { params: { limit } });
    return response.data;
  },

  async updateUser(userId, updates) {
    const response = await authClient.put(`/auth/users/${userId}`, updates);
    return response.data;
  },

  async getRoles() {
    const response = await authClient.get('/auth/roles');
    return response.data;
  },

  // Token utilities
  getToken() {
    return localStorage.getItem('rooftrack_token');
  },

  getUser() {
    const userStr = localStorage.getItem('rooftrack_user');
    return userStr ? JSON.parse(userStr) : null;
  },

  isAuthenticated() {
    const token = this.getToken();
    const user = this.getUser();
    return !!(token && user);
  },

  hasRole(roleName) {
    const user = this.getUser();
    if (!user || !user.role_ids) return false;
    
    // This would need to check against role names, simplified for now
    return user.role_ids.some(roleId => roleId.includes(roleName.toLowerCase()));
  }
};

export default authService;